import React, { useState } from 'react';
import { AI_RESEARCH_LABS } from '../data/aiResearchSkills';
import { getCourseProgress } from '../data/stanfordRLCourse';

const LearningPathCard = ({
  course,
  userProgress = {},
  onModuleToggle,
  onEnroll
}) => {
  const [expanded, setExpanded] = useState(false);
  const progress = getCourseProgress(userProgress);

  const handleModuleClick = (week) => {
    if (onModuleToggle) {
      onModuleToggle(course.id, week);
    }
  };

  return (
    <div className="card" style={{ marginBottom: '16px', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        borderBottom: '1px solid #e5e5e5',
        background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{
              fontSize: '9px',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              color: 'rgba(255,255,255,0.7)',
              marginBottom: '4px'
            }}>
              Learning Path
            </div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#fff' }}>
              {course.name}
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>
              {course.provider} • {course.duration} • {course.difficulty}
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: '#a7f3d0', fontWeight: '700', fontSize: '20px' }}>
              +${(course.totalSalaryImpact / 1000).toFixed(0)}K
            </div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)' }}>
              potential impact
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div style={{ padding: '16px', background: '#f9fafb', borderBottom: '1px solid #e5e5e5' }}>
        <div style={{
          height: '10px',
          background: '#e5e5e5',
          borderRadius: '5px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${progress.progressPercent}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #f59e0b, #10b981)',
            transition: 'width 0.3s ease'
          }} />
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '11px',
          color: '#6b7280',
          marginTop: '6px'
        }}>
          <span>{progress.completedCount}/{progress.totalCount} modules complete</span>
          <span style={{ color: '#059669', fontWeight: '600' }}>
            +${(progress.salaryGained / 1000).toFixed(0)}K earned
          </span>
        </div>
      </div>

      {/* Stats Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '1px',
        background: '#e5e5e5'
      }}>
        <div style={{ padding: '12px', background: '#fff', textAlign: 'center' }}>
          <div style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>
            {progress.hoursRemaining}h
          </div>
          <div style={{ fontSize: '10px', color: '#9ca3af' }}>remaining</div>
        </div>
        <div style={{ padding: '12px', background: '#fff', textAlign: 'center' }}>
          <div style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>
            {course.totalSkillsGained.length}
          </div>
          <div style={{ fontSize: '10px', color: '#9ca3af' }}>skills gained</div>
        </div>
        <div style={{ padding: '12px', background: '#fff', textAlign: 'center' }}>
          <div style={{ fontSize: '16px', fontWeight: '600', color: '#059669' }}>
            ${(progress.remainingSalary / 1000).toFixed(0)}K
          </div>
          <div style={{ fontSize: '10px', color: '#9ca3af' }}>to unlock</div>
        </div>
      </div>

      {/* Next Module Preview */}
      {progress.nextModule && (
        <div style={{
          padding: '16px',
          borderBottom: '1px solid #e5e5e5',
          background: 'rgba(251, 191, 36, 0.08)'
        }}>
          <div style={{
            fontSize: '10px',
            textTransform: 'uppercase',
            color: '#92400e',
            fontWeight: '600',
            marginBottom: '6px'
          }}>
            Up Next: Week {progress.nextModule.week}
          </div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
            {progress.nextModule.title}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
            {progress.nextModule.description}
          </div>
          {progress.nextModule.project && (
            <div style={{
              marginTop: '8px',
              padding: '8px 12px',
              background: '#fff',
              borderRadius: '6px',
              border: '1px solid #e5e5e5'
            }}>
              <div style={{ fontSize: '11px', color: '#6b7280' }}>Project:</div>
              <div style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                {progress.nextModule.project.name}
              </div>
              <div style={{ fontSize: '11px', color: '#059669', marginTop: '2px' }}>
                +${(progress.nextModule.project.salaryImpact / 1000).toFixed(0)}K on completion
              </div>
            </div>
          )}
        </div>
      )}

      {/* Expand/Collapse Toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%',
          padding: '12px',
          background: 'transparent',
          border: 'none',
          borderBottom: expanded ? '1px solid #e5e5e5' : 'none',
          cursor: 'pointer',
          fontSize: '12px',
          color: '#7c3aed',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px'
        }}
      >
        {expanded ? 'Hide' : 'Show'} all {course.modules.length} modules
        <span style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>
          ▼
        </span>
      </button>

      {/* Module List */}
      {expanded && (
        <div style={{ padding: '16px' }}>
          {course.modules.map((module, i) => {
            const isComplete = userProgress?.courses?.[course.id]?.completedWeeks?.includes(module.week);
            const isActive = progress.nextModule?.week === module.week;

            return (
              <div
                key={module.week}
                onClick={() => handleModuleClick(module.week)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  padding: '12px',
                  marginBottom: '8px',
                  background: isActive ? 'rgba(251, 191, 36, 0.08)' : isComplete ? 'rgba(16, 185, 129, 0.05)' : '#f9fafb',
                  border: isActive ? '1px solid #fbbf24' : isComplete ? '1px solid #10b981' : '1px solid #e5e5e5',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                {/* Week Number / Checkbox */}
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: isComplete ? '#10b981' : isActive ? '#fbbf24' : '#e5e5e5',
                  color: isComplete || isActive ? '#fff' : '#6b7280',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: '600',
                  flexShrink: 0
                }}>
                  {isComplete ? '✓' : module.week}
                </div>

                {/* Content */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: isComplete ? '#059669' : '#374151'
                  }}>
                    {module.title}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                    {module.estimatedHours}h • {module.skills.join(', ')}
                  </div>
                  {module.project && (
                    <div style={{
                      fontSize: '10px',
                      color: '#7c3aed',
                      marginTop: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      🛠 {module.project.name}
                      <span style={{ color: '#059669' }}>
                        +${(module.project.salaryImpact / 1000).toFixed(0)}K
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lab Relevance Footer */}
      <div style={{
        padding: '12px 16px',
        background: '#f9fafb',
        borderTop: '1px solid #e5e5e5',
        fontSize: '11px'
      }}>
        <span style={{ color: '#6b7280' }}>Lab relevance: </span>
        {Object.entries(course.labRelevance).map(([labKey, score]) => {
          const lab = AI_RESEARCH_LABS[labKey];
          if (!lab) return null;
          return (
            <span
              key={labKey}
              style={{
                marginLeft: '12px',
                color: lab.color
              }}
            >
              {lab.shortName}: <strong>{score}%</strong>
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default LearningPathCard;
